import React, { useState, useEffect, useRef } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { fetchAnimeData } from '../api/profileAPI';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import './AnimeListPage.css'; // You'll create this later

const AnimeListPage = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  
  // Get status filter from URL or default to 'all'
  const [activeTab, setActiveTab] = useState(
    searchParams.get('status') || 'all'
  );

  // Add sorting state
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'title');
  const [sortOrder, setSortOrder] = useState(searchParams.get('order') || 'asc');

  // Define sort options
  const sortOptions = [
    { value: 'title', label: 'Title' },
    { value: 'score', label: 'Rating' },
    { value: 'currentEpisode', label: 'Progress' },
    { value: 'updatedAt', label: 'Last Updated' }
  ];

  // Define anime statuses
  const animeStatuses = [
    { key: "all", label: "All Anime" },
    { key: "watching", label: "Watching" },
    { key: "completed", label: "Completed" },
    { key: "planning", label: "Planning" },
    { key: "onHold", label: "On Hold" },
    { key: "dropped", label: "Dropped" }
  ];

  // Define the statuses to fetch (all except 'all' which is a virtual status)
  const statusesToFetch = animeStatuses
    .filter(status => status.key !== 'all')
    .map(status => status.key);

  // Use useQueries with the correct v5 syntax
  const results = useQueries({
    queries: statusesToFetch.map(status => ({
      queryKey: ['animeData', status],
      queryFn: () => fetchAnimeData(status),
      enabled: !!user, // Always fetch all statuses for a smoother experience
      staleTime: 5 * 60 * 1000
    }))
  });

  // Check if queries are still loading
  const isLoading = results.some(result => result.isLoading);

  // Function to sort anime data
  const sortAnimeData = (data, sortKey, order) => {
    return [...data].sort((a, b) => {
      // Helper to get the actual value to compare
      const getValue = (item, key) => {
        if (key === 'title') {
          const animeDetails = item.animeId || item;
          return (animeDetails.title_english || animeDetails.title || '').toLowerCase();
        } 
        if (key === 'score') {
          return item.rating || 0;
        }
        if (key === 'updatedAt') {
          return new Date(item.updatedAt || 0).getTime();
        }
        return item[key] || 0;
      };
      
      const valueA = getValue(a, sortKey);
      const valueB = getValue(b, sortKey);
      
      if (order === 'asc') {
        return valueA > valueB ? 1 : valueA < valueB ? -1 : 0;
      } else {
        return valueA < valueB ? 1 : valueA > valueB ? -1 : 0;
      }
    });
  };

  // Combine all anime data if 'all' is selected, otherwise filter by status
  const animeData = React.useMemo(() => {
    let data;
    
    if (activeTab === 'all') {
      data = results
        .filter(result => result.isSuccess)
        .flatMap(result => result.data || []);
    } else {
      const statusIndex = statusesToFetch.indexOf(activeTab);
      data = statusIndex >= 0 && results[statusIndex]?.isSuccess 
        ? results[statusIndex].data || []
        : [];
    }
    
    // Sort the data
    return sortAnimeData(data, sortBy, sortOrder);
  }, [activeTab, results, statusesToFetch, sortBy, sortOrder]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    // Preserve sort parameters when changing tabs
    setSearchParams({ status: tab, sort: sortBy, order: sortOrder });
  };

  // Handle sort change
  const handleSortChange = (option) => {
    // If clicking the same option, toggle order
    if (option === sortBy) {
      const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
      setSortOrder(newOrder);
      setSearchParams({ status: activeTab, sort: sortBy, order: newOrder });
    } else {
      // If new option, set to that option with ascending order
      setSortBy(option);
      setSortOrder('asc');
      setSearchParams({ status: activeTab, sort: option, order: 'asc' });
    }
  };

  // Check authentication
  useEffect(() => {
    if (!loading && !user) {
      navigate("/login");
    }
  }, [user, loading, navigate]);

  // Loading state
  if (loading || isLoading) {
    return (
      <div className="anime-list-loading">
        <div className="loading-spinner"></div>
        <p>Loading your anime collection...</p>
      </div>
    );
  }

  return (
    <div className="anime-list-page">
      {/* New Title Section */}
      <div className="anime-list-page-title-container">
        <h1 className="anime-list-page-title">My Anime Collection</h1>
        <p className="anime-list-page-subtitle">
          Track, manage, and organize your anime watching journey
        </p>
      </div>

      <div className="anime-list-filters">
        <div className="anime-list-tabs">
          {animeStatuses.map(status => (
            <button
              key={status.key}
              className={`anime-list-tab ${activeTab === status.key ? 'active' : ''}`}
              onClick={() => handleTabChange(status.key)}
            >
              <span className="tab-label">{status.label}</span>
              <span className="anime-count">
                {status.key === 'all' 
                  ? results.reduce((acc, result) => 
                      acc + (result.data?.length || 0), 0)
                  : results[statusesToFetch.indexOf(status.key)]?.data?.length || 0}
              </span>
            </button>
          ))}
        </div>

        <div className="sort-controls">
          <span className="sort-label">Sort by:</span>
          <div className="sort-options">
            {sortOptions.map(option => (
              <button
                key={option.value}
                className={`sort-option ${sortBy === option.value ? 'active' : ''}`}
                onClick={() => handleSortChange(option.value)}
              >
                {option.label}
                {sortBy === option.value && (
                  <span className="sort-direction">
                    {sortOrder === 'asc' ? '↑' : '↓'}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Anime list content */}
      <div className="anime-list-content">
        {animeData.length > 0 ? (
          <div className="anime-list-grid">
            {animeData.map(anime => (
              <AnimeListItem key={anime.id} anime={anime} />
            ))}
          </div>
        ) : (
          <div className="empty-list">
            <p>No anime found in this category.</p>
          </div>
        )}
      </div>
    </div>
  );
};

// AnimeListItem Component with direct API updates (no localStorage fallback)
const AnimeListItem = ({ anime }) => {
    const [showNotePopup, setShowNotePopup] = useState(false);
    const [currentEpisode, setCurrentEpisode] = useState(anime.currentEpisode || 0);
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState(null);
    const popupRef = useRef(null);
    const queryClient = useQueryClient();
    
    // Extract anime data
    const animeDetails = anime.animeId || anime;
    const actualAnimeId = typeof animeDetails.id === 'object' ? animeDetails.id.toString() : animeDetails.id;
    
    // Check if item has a note
    const hasNote = anime.notes && anime.notes.trim() !== '';
    
    // Handle clicks outside the popup to close it
    useEffect(() => {
        function handleClickOutside(event) {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowNotePopup(false);
            }
        }
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Update episode count using direct API call (no localStorage fallback)
    const updateEpisodeCount = async (newCount) => {
        if (isUpdating) return;
        
        // Don't allow negative values
        if (newCount < 0) return;
        
        // Don't allow exceeding total episodes if episodes count is known
        if (animeDetails.episodes && newCount > animeDetails.episodes) return;
        
        setIsUpdating(true);
        setUpdateError(null);
        
        try {
            // Optimistic update for UI responsiveness
            setCurrentEpisode(newCount);
            
            // Get the token from localStorage
            const token = localStorage.getItem('token');
            if (!token) {
                setUpdateError("No authentication token found");
                throw new Error("No auth token found");
            }
            
            // API endpoint
            const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
            const endpoint = `${API_BASE_URL}/api/user/anime/${actualAnimeId}`;
            
            // Create payload
            const payload = { currentEpisode: newCount };
            
            // Check if we need to update status too
            if (animeDetails.episodes && newCount === animeDetails.episodes) {
                payload.status = 'completed';
            }
            
            console.log(`Sending API request to ${endpoint}`, payload);
            
            // Make the API call with axios
            const response = await axios.patch(endpoint, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.data.success) {
                console.log("Successfully updated episode count on server:", response.data);
                
                // Update the anime object in memory
                anime.currentEpisode = newCount;
                
                // If we completed the anime, update its status
                if (payload.status === 'completed') {
                    anime.status = 'completed';
                    queryClient.invalidateQueries(['animeData', 'completed']);
                }
                
                // Invalidate queries to refresh UI
                queryClient.invalidateQueries(['animeData']);
                queryClient.invalidateQueries(['animeData', anime.status]);
            } else {
                setUpdateError("API responded but update failed");
                console.error("API update failed:", response.data);
                // Revert UI change since API failed
                setCurrentEpisode(anime.currentEpisode || 0);
            }
        } catch (error) {
            console.error('Error updating episode count:', error);
            setUpdateError(error.message || "Update failed");
            
            // Revert UI change since API call failed
            setCurrentEpisode(anime.currentEpisode || 0);
        } finally {
            // Add a small delay for visual feedback
            setTimeout(() => {
                setIsUpdating(false);
            }, 300);
        }
    };
    
    return (
        <div className="anime-list-item">
            <div className="anime-poster">
                <img 
                    src={animeDetails.images?.webp?.large_image_url || animeDetails.image || '/default-poster.jpg'} 
                    alt={animeDetails.title} 
                    onError={(e) => { e.target.src = '/default-poster.jpg' }}
                />
                {anime.isFavorite && (
                    <div className="favorite-badge">❤️</div>
                )}
            </div>
            <div className="anime-list-details">
                <h3 className="anime-title">{animeDetails.title_english || animeDetails.title}</h3>
                <div className="anime-meta">
                    {anime.rating && (
                        <span className="anime-user-score">Your Rating: ⭐ {anime.rating}/10</span>
                    )}
                    {animeDetails.score && (
                        <span className="anime-score">Global Rating: ⭐ {animeDetails.score}/10</span>
                    )}
                    <span className="anime-status">Status: {anime.status}</span>
                    {animeDetails.episodes && (
                        <span className="anime-episodes">{animeDetails.episodes} episodes</span>
                    )}
                    {hasNote && (
                        <button 
                            className="note-toggle-btn"
                            onClick={(e) => {
                                e.preventDefault();
                                setShowNotePopup(true);
                            }}
                        >
                            See note
                        </button>
                    )}
                </div>
                <div className="anime-progress">
                    {updateError && (
                        <div className="update-error" style={{ color: '#ff5252', fontSize: '0.8rem', marginBottom: '0.5rem' }}>
                            Error: {updateError}
                        </div>
                    )}
                    <div className="episode-counter">
                        <span>Progress: </span>
                        <div className="episode-controls">
                            <button 
                                className="episode-btn minus-btn"
                                onClick={() => updateEpisodeCount(currentEpisode - 1)}
                                disabled={isUpdating || currentEpisode <= 0}
                                aria-label="Decrease episode count"
                            >
                            </button>
                            <span className={`episode-count ${isUpdating ? 'updating' : ''}`}>
                                {currentEpisode}
                            </span>
                            <button 
                                className="episode-btn plus-btn"
                                onClick={() => updateEpisodeCount(currentEpisode + 1)}
                                disabled={isUpdating || (animeDetails.episodes && currentEpisode >= animeDetails.episodes)}
                                aria-label="Increase episode count"
                            >
                            </button>
                            <span className="episode-total">/ {animeDetails.episodes || '?'}</span>
                        </div>
                    </div>
                    <div className="progress-bar">
                        <div 
                            className="progress-fill" 
                            style={{ 
                                width: `${animeDetails.episodes ? 
                                    Math.min(100, (currentEpisode / animeDetails.episodes) * 100) : 0}%` 
                            }}
                        ></div>
                    </div>
                </div>
            </div>
            <div className="anime-actions">
                <a href={`/anime/${actualAnimeId}`} className="view-details">View Details</a>
            </div>
            
            {/* Note Popup */}
            {showNotePopup && hasNote && (
                <div 
                    className="note-popup-overlay" 
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                >
                    <div 
                        className="note-popup" 
                        ref={popupRef} 
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <div className="note-popup-header">
                            <h4>{animeDetails.title_english || animeDetails.title} - Note</h4>
                            <button 
                                className="close-popup-btn"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowNotePopup(false);
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div className="note-popup-content">
                            {anime.notes}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AnimeListPage;